<template>
    <div class="container w-full min-h-screen bg-[#11111b] text-white flex flex-col">
        <Header />

        <main id="hero-section" class="relative w-full min-h-screen flex items-start justify-center p-6">
            <!-- Blurred Background -->
            <div class="absolute inset-0 bg-cover bg-center filter blur-sm z-0"
                :style="{ backgroundImage: `url(${backgroundImage})` }"></div>

            <!-- Foreground Content -->
            <div class="relative z-10 w-full max-w-5xl mx-auto flex flex-col items-start">
                <!-- Sticky Search Bar and Dropdown -->
                <div class="w-full sticky top-0 bg-[#11111b] z-20 p-4 shadow-md">
                    <div class="w-full flex flex-col sm:flex-row gap-4 mb-3">
                        <input type="text" v-model="searchQuery" placeholder="Search projects..."
                            class="w-full sm:w-3/4 p-3 text-sm sm:text-base bg-gray-800 border border-gray-700 rounded focus:outline-none focus:border-cyan-400"
                            @input="filterProjects" />
                        <select v-model="selectedCategory"
                            class="w-full sm:w-1/4 p-3 text-sm sm:text-base bg-gray-800 border border-gray-700 rounded focus:outline-none focus:border-cyan-400"
                            @change="filterProjects">
                            <option value="">All Categories</option>
                            <option v-for="category in categories" :key="category.id" :value="category.id">
                                {{ category.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Project Cards -->
                <section v-if="filteredProjects.length"
                    class="w-full grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 pt-6">
                    <router-link v-for="project in paginatedProjects" :key="project.id"
                        :to="`/project-posts/${project.id}`" class="service-card p-2 sm:p-3 rounded-lg cursor-pointer min-h-0 flex flex-col">
                        <div class="w-full aspect-square overflow-hidden rounded-md mb-2">
                            <img :src="project.imageURLs[0] || '@/assets/images/default.png'" alt="Project"
                                class="w-full h-full object-cover rounded-md" />
                        </div>
                        <h3 class="text-xs sm:text-sm md:text-base font-semibold mb-1 line-clamp-2">{{ project.title }}</h3>
                        <p class="text-xs sm:text-sm text-gray-400 flex flex-col items-start mt-auto">
                            <span>Duration: {{ project.duration || 'N/A' }}</span>
                            <span>Members: {{ project.member || 'N/A' }}</span>
                        </p>
                    </router-link>
                </section>

                <div v-else-if="isLoading" class="w-full flex flex-col items-center justify-center py-12 min-h-full">
                    <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-cyan-400"></div>
                    <p class="mt-4 text-sm sm:text-base text-gray-400">Loading projects...</p>
                </div>

                <div v-else class="w-full flex flex-col items-center justify-center py-12 min-h-full">
                    <p class="mt-4 text-sm sm:text-base text-gray-400">No projects found.</p>
                </div>

                <!-- Pagination -->
                <div class="mt-8 w-full flex justify-center">
                    <div class="relative inline-flex items-center px-4 py-2 text-sm font-semibold"
                        v-if="filteredProjects.length">
                        <SimplePagination :current-page="currentPage" :page-size="8"
                            :total-items="filteredProjects.length" @update:currentPage="currentPage = $event" />
                    </div>
                </div>
            </div>
        </main>

        <ClientFooter />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Header from '@/components/Header.vue';
import ClientFooter from '@/components/ClientFooter.vue';
import SimplePagination from '@/components/SimplePagination.vue';
import { projectFirestore } from '@/firebase/config.js';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';

const router = useRouter();

// Background Image
import backgroundImage from '@/assets/images/background.png';

// Reactive State
const searchQuery = ref('');
const selectedCategory = ref('');
const categories = ref([]);
const projects = ref([]);
const currentPage = ref(1);
const isLoading = ref(true);

const loadCategories = async () => {
    try {
        const categoriesQuery = query(collection(projectFirestore, 'categories'));
        const q = query(categoriesQuery, where('status', '==', true));
        const snapshot = await getDocs(q);
        categories.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
        console.error('Error loading categories:', err);
    }
};

const loadProjects = async () => {
    try {
        isLoading.value = true;
        const projectsQuery = query(
            collection(projectFirestore, 'projects'),
            where('status', '==', true),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(projectsQuery);
        projects.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
        console.error('Error loading projects:', err);
    } finally {
        isLoading.value = false;
    }
};

const filterProjects = () => {
    let result = projects.value;
    if (searchQuery.value) {
        result = result.filter(project =>
            project.title.toLowerCase().includes(searchQuery.value.toLowerCase())
        );
    }
    if (selectedCategory.value) {
        result = result.filter(project =>
            project.categoryId === selectedCategory.value
        );
    }
    return result;
};

const filteredProjects = computed(() => filterProjects());

const paginatedProjects = computed(() => {
    const start = (currentPage.value - 1) * 8;
    const end = start + 8;
    return filteredProjects.value.slice(start, end);
});

const goToProjectDetail = (projectId) => {
    router.push({ name: 'project-detail', params: { id: projectId } });
};

onMounted(() => {
    loadCategories();
    loadProjects();
});
</script>

<style scoped>
.container {
    width: 100%;
    max-width: none;
    min-height: 100vh;
}

.service-card {
    background-color: #1e1e2f;
    border: 2px solid #00b4d8;
    border-radius: 12px;
    color: #ffffff;
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.service-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(0, 180, 216, 0.4);
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Mobile - 2x2 Grid */
@media (max-width: 640px) {
    .service-card .aspect-square {
        max-height: 10rem;
    }
    
    .service-card {
        min-height: fit-content;
    }
}

/* Tablet - 2 Columns */
@media (min-width: 641px) and (max-width: 768px) {
    .service-card .aspect-square {
        max-height: 12rem;
    }
}

/* Medium Desktop - 3 Columns */
@media (min-width: 769px) and (max-width: 1024px) {
    .service-card .aspect-square {
        max-height: 14rem;
    }
}

/* Large Desktop - 4 Columns */
@media (min-width: 1025px) {
    .service-card .aspect-square {
        max-height: 16rem;
    }
}
</style>